import type { ComponentProps, ReactNode } from 'react';
import { Icon } from '@/lib/icons';
import { Input } from '@/components/ui/input';
import { AfricanKeyboard } from '@/components/keyboard';
import { TaskItemCard, TaskItemText } from '@/features/contribution-kit';
import type { WordItem } from '../types';
import { SpeakPhraseTranslationActions } from './SpeakPhraseTranslationActions';

type SpeakPhraseCardProps = {
  t: {
    toRead: string;
    africanKeyboard: string;
    loading: string;
    phraseDomain: string;
    phraseFileOrder: string;
    readPhraseClearly: string;
    save: string;
    cancel: string;
  };
  /** Accessible name of the moderator's edit button. */
  editLabel: string;
  wordItem?: WordItem;
  sentence: string | undefined;
  hasSentences: boolean;
  isModerator: boolean;
  selectedLanguage: string;
  isEditingPhrase: boolean;
  editedPhrase: string;
  showKeyboard: boolean;
  onEditedPhraseChange: (value: string) => void;
  onUpdatePhrase: () => void;
  onCancelPhraseEdit: () => void;
  onShowKeyboard: () => void;
  onStartPhraseEdit: () => void;
  translationActions: Omit<ComponentProps<typeof SpeakPhraseTranslationActions>, 'wordItem'>;
  /** Recording state and playback, under the phrase. */
  children?: ReactNode;
  footer?: ReactNode;
};

const iconButton =
  'inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-border/60 bg-surface text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The phrase to read, with the moderator's inline fix and the translation actions. */
export function SpeakPhraseCard({
  t,
  editLabel,
  wordItem,
  sentence,
  hasSentences,
  isModerator,
  selectedLanguage,
  isEditingPhrase,
  editedPhrase,
  showKeyboard,
  onEditedPhraseChange,
  onUpdatePhrase,
  onCancelPhraseEdit,
  onShowKeyboard,
  onStartPhraseEdit,
  translationActions,
  children,
  footer,
}: SpeakPhraseCardProps) {
  return (
    <div className="space-y-3">
      <TaskItemCard
        label={t.toRead}
        corner={
          isModerator && hasSentences && !isEditingPhrase ? (
            <button type="button" onClick={onStartPhraseEdit} className={iconButton} aria-label={editLabel}>
              <Icon name="edit" className="size-4" />
            </button>
          ) : null
        }
        footer={footer}
      >
        {isEditingPhrase ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                value={editedPhrase}
                onChange={(e) => onEditedPhraseChange(e.target.value)}
                className="h-11 text-base"
                autoFocus
              />
              <button type="button" onClick={onUpdatePhrase} className={`${iconButton} text-success`} title={t.save} aria-label={t.save}>
                <Icon name="check" className="size-4" />
              </button>
              <button type="button" onClick={onCancelPhraseEdit} className={iconButton} title={t.cancel} aria-label={t.cancel}>
                <Icon name="x" className="size-4" />
              </button>
            </div>
            {!showKeyboard && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onShowKeyboard}
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-border/60 px-4 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Icon name="settings" className="size-4" />
                  {t.africanKeyboard}
                </button>
              </div>
            )}
          </div>
        ) : (
          <TaskItemText quoted={hasSentences}>{hasSentences ? sentence : t.loading}</TaskItemText>
        )}

        {(wordItem?.domain || wordItem?.fileOrder !== undefined) && (
          <p className="break-all text-center text-xs text-faint-foreground">
            {wordItem.domain && `${t.phraseDomain}: ${wordItem.domain}`}
            {wordItem.domain && wordItem.fileOrder !== undefined && ' · '}
            {wordItem.fileOrder !== undefined && `${t.phraseFileOrder}: ${wordItem.fileOrder}`}
          </p>
        )}

        <p className="text-center text-sm text-muted-foreground">{t.readPhraseClearly}</p>

        {children}

        {wordItem && (
          <div className="border-t border-border/60 pt-4">
            <SpeakPhraseTranslationActions {...translationActions} wordItem={wordItem} />
          </div>
        )}
      </TaskItemCard>

      {isEditingPhrase && showKeyboard && (
        <AfricanKeyboard
          language={selectedLanguage}
          value={editedPhrase}
          onChange={onEditedPhraseChange}
          onClose={onCancelPhraseEdit}
        />
      )}
    </div>
  );
}
